// Corresponds to WorkflowStatus enum in backend
export type WorkflowStatus =
  | 'OrderReceived'       // Initial order data received from Shopee API 3.1.
  | 'InfoReceived'        // Detailed parcel info received from Shopee API 3.4.
  | 'ManifestPending'     // Ready for internal manifest preparation or other initial processing.
  | 'LoadingDataReady'    // Data required for Carton Loading API (3.6) is ready.
  | 'LoadingSent'         // Carton Loading information sent to Shopee API 3.6.
  | 'TrackingUpdated'     // Tracking information has been sent to Shopee API.
  | 'Shipped'             // Shipment is physically in transit.
  | 'Arrived'             // Cartons arrived at warehouse (scanned by mobile scanner).
  | 'CustomsProcessing'   // Customs processing is underway.
  | 'CustomsCleared'      // Customs clearance completed.
  | 'ReadyForDelivery'    // Ready for last-mile delivery.
  | 'Delivering'          // Out for delivery.
  | 'Delivered'           // Delivery completed.
  | 'Completed'           // Order processing completed.
  | 'Cancelled'           // Order was cancelled.
  | 'Error';              // An error occurred during processing.
// ... existing code ...
export interface OrderSummaryDto {
  uniqueId: string; // Primary key (matches API response)
  shopeeOrderNo: string;
  carrierTrackingNumber: string | null;
  cartonNo: string | null;
  status: string; // This will be one of the WorkflowStatus values
  createdAt: Date;
  lastUpdated: Date | null;
  destinationRegion: string | null;
  parcelCount: number;
}

export interface ReceiverInfoDto {
  name: string | null;
  phone: number | null;
  zipcode: number | null;
  region: string | null;
  region_code: string | null;
  state: string | null;
  city: string | null;
  address: string | null;
  email?: string | null;
  district?: string | null; // Optional, if your backend supports it
  street?: string | null; // Optional, if your backend supports it
}

export interface SenderInfoDto {
  name: string | null;
  phone: number | null;
  zipcode: number | null;
  region: string | null;
  region_code: string | null; // Assuming region_code is a string, adjust if it's a number
  state: string | null;
  city: string | null;
  address: string | null;
  email?: string | null; // Optional, if your backend supports it
  district?: string | null;
  street?: string | null; // Optional, if your backend supports it
}

export interface ParcelReceiverInfoDto {
  receiver_name: string | null;
  receiver_phone: string | null;
  receiver_postalCode: string | null;
  receiver_region: string | null;
  receiver_state: string | null;
  receiver_city: string | null;
  receiver_address: string | null;
  receiver_district?: string | null; // Optional, if your backend supports it
  receiver_street?: string | null; // Optional, if your backend supports it
  //receiver_declare_name?: string | null; // Optional, if your backend supports it
  //receiver_declare_phone?: string | null; // Optional, if your backend supports it
}

export interface ParcelSenderInfoDto {
  sender_name: string | null;
  sender_phone: string | null;
  sender_postalCode: string | null;
  sender_region_code: string | null; // Assuming region_code is a string, adjust if it's a number
  sender_state: string | null;
  sender_city: string | null;
  sender_address: string | null;
  sender_district?: string | null;
  sender_street?: string | null; // Optional, if your backend supports it
}

export interface PreCarrierInfoDto {
  carrier_code: number;
  carrier_name: string;
  carrier_tn?: string | null;
  carrier_ext_tn?: string | null;
}

export interface NextCarrierInfoDto {
  carrier_code: number;
  carrier_name: string;
  carrier_tn?: string | null;
  carrier_ext_tn?: string | null;
}

export interface ParcelDto {
  trackingNumber: string; // Primary key (matches API response)
  shopeeOrderNo?: string; // Foreign key reference (matches API response)
  weight: number | null;
  length: number | null;
  width: number | null;
  height: number | null;
  volume: number | null;
  serviceCode?: string | null;
  destinationRegion?: string | null;
  originalCartonNo?: string | null;
  status?: string | null;
  // Owned entities with new structure - keeping existing structure for backward compatibility
  Receiver_info?: ParcelReceiverInfoDto | null;
  Sender_info?: ParcelSenderInfoDto | null;
  Sku_list?: SkuInfoDto[]; // Array of SKU items
}

// New SkuInfo interface for owned entity
export interface SkuInfoDto {
  Id: string;
  trackingNumber: string; // Foreign key to Parcel (matches API response)
  sku_name?: string | null;
  sku_quantity?: number | null;
  sku_weight?: number | null;
  sku_value?: number | null;
}

// Represents a repacked carton, often a child of a main order
export interface RepackedCartonDto {
  uniqueId: string; // A unique identifier for this repacked carton entry in your system
  cartonNo: string; // The tracking number or identifier of this repacked carton
  shopeeOrderNo: string; // The ILH Shopee Order No specifically for this repacked carton
  destinationRegion?: string | null;
  cartonWeight?: number | null;
  // Assuming goodsType and transportType are stored as numeric codes in the backend for repacked cartons
  // If they are strings like in OrderDetailDto, adjust accordingly.
  // For consistency with RepackCreateRequestDto, let's aim for numbers here if possible,
  // or handle conversion carefully. Let's assume string for now to match OrderDetailDto.
  goodsType: string | null; // e.g., "0" for Non-DG, "1" for DG
  transportType: string | null; // e.g., "1" for Air, "2" for Sea
  serviceCode?: string | null;
  parcels?: ParcelDto[]; // Parcels contained within this repacked carton
  cartonLength: number | null;
  cartonWidth: number | null;
  cartonHeight: number | null;
  cartonVolume: number | null;
  // ... other properties like dimensions, status, etc.
}

// Add this interface near your other DTOs
export interface EligibleCartonDto {
  uniqueId: string;
  shopeeOrderNo: string;
  cartonNo: string;
  carrierTrackingNumber: string;
  status: string;
  serviceCode: string;
  destinationRegion: string;
  destinationRegionName: string;
  createdAt: string;
  lastUpdated: string;
  parcelCount: number;
  physicalDetails: CartonPhysicalDetailsDto;
  summary?: string; // Added client-side for dropdown display
}

export interface CartonPhysicalDetailsDto {
  weight: number;
  length: number;
  width: number;
  height: number;
  volume: number;
}

export interface EligibleCartonsRequest {
  serviceCode: string;
  destinationRegion?: string;
  originalCartonId?: string;
  page: number;
  pageSize: number;
}

export interface OrderDetailDto {
  uniqueId: string; // Primary key (matches API response)
  shopeeOrderNo: string;
  carrierTrackingNumber: string | null;
  cartonNo: string | null;
  cartonWeight: number | null;
  cartonLength: number | null;
  cartonWidth: number | null;
  cartonHeight: number | null;
  cartonVolume: number | null;
  goodsType: string | null;
  serviceCode: string | null;
  transportType: string | null;
  destinationRegionName: string | null;
  destinationRegion: string | null;
  status: string; // This will be one of the WorkflowStatus values
  createdAt: Date;
  lastUpdated: Date | null;
  parcels: ParcelDto[];
  repackedCartons?: RepackedCartonDto[];
  // Embedded address fields (new structure)
  Sender: SenderInfoDto;
  Receiver: ReceiverInfoDto;
  pre_carrier_info: PreCarrierInfoDto; // Updated naming
  next_carrier_info: NextCarrierInfoDto; // Updated naming
}

export interface PaginatedResponse<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface RepackCreateRequestDto {
  carton_no: string; // New carton number
  carton_weight?: number; // Backend uses double?
  carton_length?: number;
  carton_width?: number;
  carton_height?: number;
  carton_volume?: number; // Often calculated, but can be sent

  destination_region: string;
  goods_type: number; // e.g., 0 for non-DG, 1 for DG
  service_code?: string;
  transport_type: number; // e.g., 1 for Air, 2 for Sea

  parcel_list: string[]; // List of parcel tracking numbers


  sender?: SenderInfoDto;       // Added
  receiver?: ReceiverInfoDto;   // Added
  preCarrierInfo?: PreCarrierInfoDto; // Added
  nextCarrierInfo?: NextCarrierInfoDto; // Added
  original_shopee_order_no?: string; // Keep if used
  original_order_id?: string;      // Keep if used
  
}

// For POST api/orders/repack_update
// Matches your backend's internal RepackUpdateRequest DTO
export interface ParcelActionDto {
  parcelTrackingNumber: string;           // Changed from ParcelTrackingNumber
  actionType: number;   
}

export enum FrontendRepackCancelParcelAction { // Mirroring backend enum
  UnbindOnly = 0, // Or map to backend's actual enum values if they are not 0,1,2
  MoveToNewCarton = 1,
  MoveToExistingCarton = 2
}

export interface RepackUpdateRequestDto {
    originalRepackedCartonNo: string;       // Changed from OriginalRepackedCartonNo  
    ilhShopeeNo: string;                    // Changed from IlhShopeeNo
    destinationRegion: string;              // Changed from DestinationRegion
    cartonWeight: number;                   // Already correct
    cartonLength: number;                   // Already correct
    cartonWidth: number;                    // Already correct
    cartonHeight: number;                   // Already correct
    cartonVolume: number;                   // Already correct
    parcelActions: ParcelActionDto[];   
     reactivateIfCancelled?: boolean;
}

// For POST api/orders/repack_cancel
// Matches your backend's internal RepackCancelRequest DTO
export interface RepackCancelRequestDto {
  repackedCartonNo: string; // The carrier_tn of the repacked order to cancel
  ilhShopeeNo?: string; // Optional
  destinationRegion?: string; // Optional, might be needed by Shopee API
  reasonCode?: string; // Or string, depending on Shopee API requirements
  parcelAction: FrontendRepackCancelParcelAction; // Actions to take on parcels in the repacked carton
}

export interface StatusUpdateRequestDto {
  status: string; // Should match one of the WorkflowStatus values
}

export interface GetOrdersParams {
  status?: WorkflowStatus | string;
  dateFrom?: string;
  dateTo?: string;
  searchTerm?: string;
  page?: number;
  pageSize?: number;
}